import React from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '@/lib/auth'
import { useIsMobile } from '@/lib/useIsMobile'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { formatCurrency, formatRelativeDate } from '@/lib/utils'
import { Inbox, TrendingUp, FileText, Star, Zap, ArrowUpRight, Clock, CheckCircle2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

function StatCard({ label, value, change, icon: Icon, color, path }: any) {
  const navigate = useNavigate()
  return (
    <motion.div
      whileHover={{ y: -2 }}
      onClick={() => path && navigate(path)}
      style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '20px 22px', cursor: path ? 'pointer' : 'default', position: 'relative', overflow: 'hidden', transition: 'border-color 200ms ease' }}
      onMouseEnter={e => { if (path) e.currentTarget.style.borderColor = 'var(--accent-border)' }}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
    >
      <div style={{ position: 'absolute', top: -24, right: -24, width: 80, height: 80, background: `radial-gradient(circle, color-mix(in srgb, ${color} var(--status-tint-fill), transparent) 0%, transparent 70%)`, pointerEvents: 'none' }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: `color-mix(in srgb, ${color} var(--status-tint-dim), transparent)`, border: `1px solid color-mix(in srgb, ${color} var(--status-tint-fill), transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon style={{ width: 16, height: 16, color }} />
        </div>
        {change !== undefined && (
          <span style={{ fontSize: 11, fontWeight: 600, color: change >= 0 ? 'var(--positive)' : 'var(--danger)' }}>
            {change >= 0 ? '+' : ''}{change}%
          </span>
        )}
      </div>
      <p style={{ fontSize: 28, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: '0 0 4px', lineHeight: 1 }}>{value}</p>
      <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>{label}</p>
    </motion.div>
  )
}

export default function Dashboard() {
  const { profile, workspace } = useAuth()
  const isMobile = useIsMobile()
  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return null
      const [conversations, deals, invoices, contacts] = await Promise.all([
        supabase.from('conversations').select('id, status, priority, created_at', { count: 'exact' }).eq('workspace_id', workspace.id).eq('status', 'open'),
        supabase.from('deals').select('id, value, status', { count: 'exact' }).eq('workspace_id', workspace.id).eq('status', 'open'),
        supabase.from('invoices').select('id, total, status', { count: 'exact' }).eq('workspace_id', workspace.id).in('status', ['sent', 'viewed', 'overdue']),
        supabase.from('contacts').select('id', { count: 'exact' }).eq('workspace_id', workspace.id),
      ])
      return {
        openConversations: conversations.count || 0,
        openDeals: deals.count || 0,
        openDealValue: deals.data?.reduce((s, d) => s + (d.value || 0), 0) || 0,
        outstandingInvoices: invoices.count || 0,
        outstandingValue: invoices.data?.reduce((s, i) => s + (i.total || 0), 0) || 0,
        totalContacts: contacts.count || 0,
      }
    },
    enabled: !!workspace?.id,
  })

  const { data: recentConversations } = useQuery({
    queryKey: ['recent-conversations', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('conversations').select('*').eq('workspace_id', workspace.id).eq('status', 'open').order('updated_at', { ascending: false }).limit(5)
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const navigate = useNavigate()

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1200 }}>
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 26, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: '0 0 4px' }}>
          {greeting}, {profile?.full_name?.split(' ')[0] || 'there'} 👋
        </h1>
        <p style={{ fontSize: 14, color: 'var(--ink-tertiary)', margin: 0 }}>
          Here's what's happening in {workspace?.name}
        </p>
      </motion.div>

      {/* Stats grid */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 32 }}
      >
        <StatCard label="Open conversations" value={stats?.openConversations ?? '—'} color="var(--accent)" icon={Inbox} path="/app/inbox" />
        <StatCard label="Active deals" value={stats?.openDeals ?? '—'} color="var(--info)" icon={TrendingUp} path="/app/deals" />
        <StatCard label="Outstanding invoices" value={stats ? formatCurrency(stats.outstandingValue) : '—'} color="var(--positive)" icon={FileText} path="/app/invoices" />
        <StatCard label="Total contacts" value={stats?.totalContacts ?? '—'} color="var(--warning)" icon={Star} path="/app/contacts" />
      </motion.div>

      {/* Recent activity. Desktop: main column + 320px right rail. Mobile
          (<768px): single column — the rail stacks below the conversations
          card. */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 320px', gap: 20 }}>
        {/* Conversations */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>Open conversations</h3>
              <button onClick={() => navigate('/app/inbox')} style={{ fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                View all <ArrowUpRight style={{ width: 12, height: 12 }} />
              </button>
            </div>
            <div>
              {recentConversations && recentConversations.length > 0 ? (
                recentConversations.map((conv: any) => (
                  <div key={conv.id} onClick={() => navigate(`/app/inbox/${conv.id}`)}
                    style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', transition: 'background 150ms ease', display: 'flex', alignItems: 'center', gap: 12 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--conversation-avatar-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--accent-ink)', flexShrink: 0 }}>
                      {(conv.contact_name || 'U')[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conv.contact_name || 'Unknown'}</p>
                        <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', flexShrink: 0, marginLeft: 8 }}>{formatRelativeDate(conv.updated_at)}</span>
                      </div>
                      <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {conv.last_message || conv.subject || 'No messages yet'}
                      </p>
                    </div>
                    {conv.priority === 'urgent' && (
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--danger)', flexShrink: 0 }} />
                    )}
                  </div>
                ))
              ) : (
                <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                  <CheckCircle2 style={{ width: 32, height: 32, color: 'var(--positive)', margin: '0 auto 10px', display: 'block' }} />
                  <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>All clear</p>
                  <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>No open conversations</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Quick actions */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '16px 20px' }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 14px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Quick actions</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { label: 'New conversation', icon: Inbox, path: '/app/inbox', color: 'var(--accent)' },
                { label: 'Create invoice', icon: FileText, path: '/app/invoices', color: 'var(--positive)' },
                { label: 'Add contact', icon: Star, path: '/app/contacts', color: 'var(--info)' },
                { label: 'New deal', icon: TrendingUp, path: '/app/deals', color: 'var(--warning)' },
              ].map((action, i) => (
                <button key={i} onClick={() => navigate(action.path)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--border-subtle)', cursor: 'pointer', transition: 'all 150ms ease', textAlign: 'left', width: '100%' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--border-subtle)'; e.currentTarget.style.borderColor = `color-mix(in srgb, ${action.color} var(--status-tint-strong), transparent)` }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.borderColor = 'var(--border-subtle)' }}
                >
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: `color-mix(in srgb, ${action.color} var(--status-tint-faint), transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <action.icon style={{ width: 13, height: 13, color: action.color }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-secondary)' }}>{action.label}</span>
                  <ArrowUpRight style={{ width: 12, height: 12, color: 'var(--ink-tertiary)', marginLeft: 'auto' }} />
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
